/** Safari on iPhone exposes fullscreen on the video, not its wrapper. */
export type FullscreenVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
};

export async function toggleMediaFullscreen(container: HTMLElement, video: FullscreenVideo, doc: Document): Promise<void> {
  if (video.webkitDisplayingFullscreen) {
    if (!video.webkitExitFullscreen) throw new Error("Fullscreen exit unavailable");
    video.webkitExitFullscreen();
    return;
  }
  if (doc.fullscreenElement === container) {
    await doc.exitFullscreen();
    return;
  }
  // Call the native method directly within the tap gesture, before any await.
  if (video.webkitEnterFullscreen && (!doc.fullscreenEnabled || !container.requestFullscreen)) {
    video.webkitEnterFullscreen();
    return;
  }
  if (container.requestFullscreen) {
    try {
      await container.requestFullscreen();
      return;
    } catch (error) {
      if (!video.webkitEnterFullscreen) throw error;
    }
  }
  if (!video.webkitEnterFullscreen) throw new Error("Fullscreen unavailable");
  video.webkitEnterFullscreen();
}

/** Whether the element-level Fullscreen API can be used (not on iPhone Safari). */
export function fullscreenSupported(element: HTMLElement | null, doc: Document): boolean {
  return Boolean(element && doc.fullscreenEnabled && typeof element.requestFullscreen === "function");
}

/**
 * Put `element` into real fullscreen, or leave it. Resolves to true when the
 * browser honoured the request; false means the caller should fall back to a
 * CSS takeover of the viewport.
 */
export async function toggleElementFullscreen(element: HTMLElement, doc: Document, timeoutMs = 1000): Promise<boolean> {
  if (doc.fullscreenElement === element) {
    await doc.exitFullscreen();
    return true;
  }
  if (!fullscreenSupported(element, doc)) return false;
  // Some embedded browsers never settle the request; give up after a moment.
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), timeoutMs);
  });
  try {
    return await Promise.race([element.requestFullscreen().then(() => true), timeout]);
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
